import { useAppRoles } from "@/hooks/use-app-roles";
import { Badge, Card, CardContent, CardHeader, CardTitle, Skeleton } from "@fox1ck-org/ui";
import { Users, KeyRound, Network } from "lucide-react";
import { OrgUnitTypeBadge } from "@/components/shared/OrgUnitTypeBadge";
import { RoleBadge } from "@/components/shared/RoleBadge";
import type { OrgUnitType } from "@/types/org";

interface UserAccessOverviewProps {
  userId: string;
  userGroups: Array<{ id: string; name: string; path: string }>;
}

function parseOrgGroups(groups: Array<{ name: string; path: string }>) {
  // Parse /orgs/{company}/departments/{dept}/teams/{team} patterns
  const orgMemberships: Array<{ name: string; type: OrgUnitType; path: string }> = [];

  for (const g of groups) {
    const path = g.path;
    if (!path.startsWith("/orgs/")) continue;

    const parts = path.split("/").filter(Boolean); // ["orgs", "vibe", "departments", "marketing", "teams", "alpha"]

    if (parts.length === 2 && parts[1]) {
      orgMemberships.push({ name: parts[1], type: "company", path });
    } else if (parts.length === 4 && parts[2] === "departments" && parts[3]) {
      orgMemberships.push({ name: parts[3], type: "department", path });
    } else if (parts.length === 4 && parts[2] === "teams" && parts[3]) {
      orgMemberships.push({ name: parts[3], type: "team", path });
    } else if (parts.length === 6 && parts[4] === "teams" && parts[5]) {
      orgMemberships.push({ name: parts[5], type: "team", path });
    }
  }

  return orgMemberships;
}

export function UserAccessOverview({ userId: _userId, userGroups }: UserAccessOverviewProps) {
  const { data: appRolesData, isLoading: rolesLoading } = useAppRoles();

  const orgMemberships = parseOrgGroups(userGroups);
  const nonOrgGroups = userGroups.filter((g) => !g.path.startsWith("/orgs/"));
  const appBindings = appRolesData?.items ?? [];

  return (
    <div className="space-y-6">
      {/* Org Memberships */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Network className="h-4 w-4 text-muted-foreground" />
            <CardTitle>Участие в подразделениях</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          {orgMemberships.length === 0 ? (
            <p className="text-sm text-muted-foreground">Не состоит в подразделениях</p>
          ) : (
            <div className="space-y-2">
              {orgMemberships.map((m) => (
                <div key={m.path} className="flex items-center justify-between rounded-lg bg-muted/50 p-3">
                  <div className="flex items-center gap-3">
                    <OrgUnitTypeBadge type={m.type} />
                    <div>
                      <p className="text-sm font-medium text-foreground capitalize">{m.name}</p>
                      <p className="text-xs text-muted-foreground font-mono">{m.path}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Platform Groups (non-org) */}
      {nonOrgGroups.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-muted-foreground" />
              <CardTitle>Группы платформы</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {nonOrgGroups.map((g) => (
                <Badge key={g.id} variant="neutral" className="text-sm">
                  {g.name}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* App Role Bindings */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-muted-foreground" />
            <CardTitle>Доступные роли приложений</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          {rolesLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : appBindings.length === 0 ? (
            <p className="text-sm text-muted-foreground">Привязок ролей нет</p>
          ) : (
            <div className="space-y-2">
              {appBindings.map((binding) => (
                <div key={binding.name} className="flex items-center justify-between rounded-lg bg-muted/50 p-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <KeyRound className="h-4 w-4 text-muted-foreground shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground">{binding.clientId}</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {binding.roles.map((r) => (
                          <RoleBadge key={r.name} name={r.name} description={r.description} />
                        ))}
                      </div>
                    </div>
                  </div>
                  <Badge variant={binding.status.ready ? "ok" : "neutral"} className="shrink-0 ml-2">
                    {binding.status.ready ? "Готово" : "Ожидание"}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
